import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, Button, Select, Skeleton } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import {
  EPresenceStatusApi,
  EPresenceStatus,
  EStatus,
} from '@/shared/enums/presence.enum';
import { SipJs } from '@/services/sip-js.service';
import { LOCAL_STORAGE } from '@/shared/constants/localStorage';
import { useWrapUpTimer } from '@/hooks/useWrapUpTimer';
import './Presence.scss';

const { Option } = Select;

interface PresenceProps {
  status: EPresenceStatusApi;
  isSelectable?: boolean;
}

export function Presence({ status, isSelectable = true }: PresenceProps) {
  const { wrapUp } = useWrapUpTimer();
  const location = useLocation();
  const navigate = useNavigate();
  let backBtn = <></>;

  const getStatusDisplayText = (currentStatus: EPresenceStatusApi): string => {
    if (currentStatus === EPresenceStatusApi.ON_A_CALL) {
      return EPresenceStatus.CALL;
    }
    if (currentStatus === EPresenceStatusApi.WRAP_UP) {
      return EPresenceStatus.WRAP_UP;
    }
    return currentStatus;
  };

  const getStatusBadgeType = (status: EPresenceStatusApi): EStatus => {
    if (status === EPresenceStatusApi.ON_A_CALL) {
      return EStatus.SUCCESS;
    }
    if (status === EPresenceStatusApi.WRAP_UP) {
      return EStatus.PROCESSING;
    }
    switch (status) {
      case EPresenceStatusApi.AVAILABLE:
        return EStatus.SUCCESS;
      case EPresenceStatusApi.BUSY:
      case EPresenceStatusApi.NOT_DISTURB:
      case EPresenceStatusApi.BREAK:
        return EStatus.ERROR;
      case EPresenceStatusApi.AWAY:
      case EPresenceStatusApi.BERIGHTBACK:
        return EStatus.WARNING;
      default:
        return EStatus.DEFAULT;
    }
  };

  const onChangePresence = (presenceStatus: EPresenceStatusApi) => {
    localStorage.setItem(LOCAL_STORAGE.LS_MANUAL_PRESENCE, presenceStatus);
    SipJs.pushNotification(presenceStatus, true);
  };

  const backToHistory = () => {
    navigate('/activity-history');
  };

  const backToMarketingList = () => {
    navigate('/dialer');
  };

  const back = () => {
    navigate(-1);
  };

  if (location.pathname === '/call-record') {
    backBtn = (
      <Button
        data-testid="record-back-to-history"
        type="text"
        icon={<LeftOutlined />}
        onClick={backToHistory}
      />
    );
  }

  if (location.pathname.includes('/dialer/')) {
    backBtn = (
      <Button
        type="text"
        data-testid="dialer-back-to-marketing"
        icon={<LeftOutlined />}
        onClick={backToMarketingList}
      />
    );
  }

  if (
    location.pathname.includes('/dialer-view') ||
    location.pathname.includes('/settings')
  ) {
    backBtn = (
      <Button
        data-testid="settings-back-button"
        type="text"
        icon={<LeftOutlined />}
        onClick={back}
      />
    );
  }

  if (['/outbound', '/inbound', '/login'].includes(location.pathname)) {
    return;
  }

  const inactiveAppState = location.pathname === '/inactive';
  if (inactiveAppState) {
    return <div className="presence"></div>;
  } else {
    if (!status) {
      return (
        <div className="presence">
          <Skeleton.Button
            style={{ width: 100, marginLeft: 10, marginTop: 5 }}
            active={true}
            size="small"
            shape="default"
            block={true}
          />
          {backBtn}
        </div>
      );
    }
  }

  const currentStatusDisplayText: string = getStatusDisplayText(status);
  const currentBadgeStatus: EStatus = getStatusBadgeType(status);

  // We don't show 'On a Call' and 'Wrap up' statuses in a select, only as a plain text in div.
  // But we need to have an ability to switch those statuses to other statuses
  // when port sip sends them under certain conditions.
  return (
    <div className={isSelectable ? 'presence' : 'presence--not-selectable'}>
      {(status === EPresenceStatusApi.ON_A_CALL && SipJs.sessions.length > 0) ||
      (status === EPresenceStatusApi.WRAP_UP && wrapUp.show) ? (
        <div className="hidden-presence-select">
          <Badge status={currentBadgeStatus} text={currentStatusDisplayText} />
        </div>
      ) : (
        <Select
          className="presence-select"
          showArrow={isSelectable}
          disabled={!isSelectable}
          value={status}
          onChange={onChangePresence}
          bordered={false}
        >
          {!SipJs.sessions.length &&
            status === EPresenceStatusApi.ON_A_CALL && (
              <Option value={EPresenceStatusApi.ON_A_CALL} disabled>
                <Badge status={EStatus.SUCCESS} text={EPresenceStatus.CALL} />
              </Option>
            )}
          {!wrapUp.show && status === EPresenceStatusApi.WRAP_UP && (
            <Option value={EPresenceStatusApi.WRAP_UP} disabled>
              <Badge
                status={currentBadgeStatus}
                text={EPresenceStatus.WRAP_UP}
              />
            </Option>
          )}
          <Option value={EPresenceStatusApi.AVAILABLE}>
            <Badge status={EStatus.SUCCESS} text={EPresenceStatus.AVAILABLE} />
          </Option>
          <Option value={EPresenceStatusApi.BUSY}>
            <Badge status={EStatus.ERROR} text={EPresenceStatus.BUSY} />
          </Option>
          <Option value={EPresenceStatusApi.NOT_DISTURB}>
            <Badge status={EStatus.ERROR} text={EPresenceStatus.NOT_DISTURB} />
          </Option>
          <Option value={EPresenceStatusApi.BREAK}>
            <Badge status={EStatus.ERROR} text={EPresenceStatus.BREAK} />
          </Option>
          <Option value={EPresenceStatusApi.AWAY}>
            <Badge status={EStatus.WARNING} text={EPresenceStatus.AWAY} />
          </Option>
          <Option value={EPresenceStatusApi.BERIGHTBACK}>
            <Badge
              status={EStatus.WARNING}
              text={EPresenceStatus.BERIGHTBACK}
            />
          </Option>
          <Option value={EPresenceStatusApi.OFFLINE}>
            <Badge status={EStatus.DEFAULT} text={EPresenceStatus.OFFLINE} />
          </Option>
        </Select>
      )}
      {backBtn}
    </div>
  );
}
